import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Copy,
  KeyRound,
  Loader2,
  LogOut,
  Plus,
  X as XIcon,
  ExternalLink,
} from 'lucide-react';
import { getEmbeddedConnectedWallet, useExportWallet, useWallets } from '@privy-io/react-auth';
import { DiscordIcon, TelegramIcon, XLogoIcon } from '@/components/icons/BrandIcons';
import Modal from '@/components/ui/Modal';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { useUiStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { isBackendConfigured, logout } from '@/lib/api';
import { isInAppBrowser, openInBrowserHref } from '@/lib/inAppBrowser';
import { queryClient } from '@/lib/queryClient';
import { runPrivyLogout } from '@/lib/runtime';
import { useBigwBalance, walletPortalUrl } from '@/lib/wallet';
import {
  useDiscordCallbackStatus,
  useLinkDiscord,
  useLinkX,
  useTelegramLoginWidget,
  useUnlinkDiscord,
  useUnlinkX,
  useXCallbackStatus,
  useUnlinkTelegram,
} from '@/hooks/useSocialLink';
import { useProfile } from '@/hooks/useProfile';
import { useParticipantProfile } from '@/hooks/useParticipantProfile';
import { useQuestTotals } from '@/hooks/useQuestTotals';
import { cn, formatPoints, truncateAddress } from '@/lib/utils';
import type { SocialProvider } from '@/lib/types';

interface SocialRow {
  provider: SocialProvider;
  label: string;
  Icon: typeof XLogoIcon;
}

const SOCIALS: SocialRow[] = [
  { provider: 'x', label: 'X', Icon: XLogoIcon },
  { provider: 'discord', label: 'Discord', Icon: DiscordIcon },
  { provider: 'telegram', label: 'Telegram', Icon: TelegramIcon },
];

type CallbackNotice = { tone: 'success' | 'error'; message: string };

/**
 * Maps the ?discord=…|x=…&reason=… OAuth callback the backend appends after
 * redirecting back here into a human-readable notice. `reason` codes mirror
 * app/routers/social.py; unknown codes fall back to a generic message.
 */
function socialCallbackNotice(
  label: string,
  status: 'ok' | 'error' | null,
  reason: string | null,
): CallbackNotice | null {
  if (status === null) return null;
  if (status === 'ok') return { tone: 'success', message: `${label} connected.` };

  const byReason: Record<string, string> = {
    conflict: `This ${label} account is already linked to another wallet.`,
    invalid_state: `Your ${label} link session expired. Please try again.`,
    missing_params: `Your ${label} link session expired. Please try again.`,
    user_not_found: 'Account not found. Reconnect your wallet and try again.',
    missing_user_id: `Couldn't read your ${label} account. Please try again.`,
    oauth_failed: `Couldn't connect to ${label}. Please try again.`,
    access_denied: `${label} authorization was cancelled.`,
  };
  return {
    tone: 'error',
    message: (reason && byReason[reason]) || `Couldn't connect to ${label}. Please try again.`,
  };
}

/**
 * Shown in in-app browsers (Telegram, Instagram, wallet apps…), where X /
 * Discord sign-in and the Telegram login popup often fail: offers to reopen
 * this page in the real browser, or to copy its link.
 */
function InAppBrowserHint() {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);

  const pageUrl = window.location.href;
  const openHref = openInBrowserHref(pageUrl);

  const onCopyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
    } catch {
      /* clipboard unavailable — silent */
    }
  };

  return (
    <div className="flex flex-col gap-2 border-b border-line bg-amber2-50 px-4 py-3">
      <p className="text-xs text-amber2-600">
        Linking accounts works best in your browser. This app&apos;s built-in browser may not finish
        signing you in.
      </p>
      <div className="flex flex-wrap gap-2">
        {openHref && (
          <a href={openHref} className="btn-water px-3 py-1.5 text-xs">
            Open in browser
          </a>
        )}
        <button type="button" onClick={onCopyLink} className="btn-ghost px-3 py-1.5 text-xs">
          {copied ? 'Link copied' : 'Copy link'}
        </button>
      </div>
    </div>
  );
}

function ProfileModal() {
  const open = useUiStore((s) => s.profileModalOpen);
  const openProfile = useUiStore((s) => s.openProfile);
  const closeProfile = useUiStore((s) => s.closeProfile);
  const storedProfile = useAuthStore((s) => s.profile);
  const setProfile = useAuthStore((s) => s.setProfile);
  const reset = useAuthStore((s) => s.reset);

  // When the modal is open, keep an up-to-date profile from /auth/me so that
  // social link/unlink mutations (which invalidate the 'me' query) flow back
  // into the rendered state.
  const { data: liveProfile } = useProfile();
  const profile = liveProfile ?? storedProfile;
  const { data: participant } = useParticipantProfile();
  const { completed: missionsCompleted, total: missionsTotal } = useQuestTotals();

  // Mirror the latest live profile back into the store so consumers outside
  // this modal (header, stats row) stay in sync.
  useEffect(() => {
    if (liveProfile && liveProfile !== storedProfile) {
      setProfile(liveProfile);
    }
  }, [liveProfile, storedProfile, setProfile]);

  const [copied, setCopied] = useState(false);
  const tgContainerRef = useRef<HTMLDivElement>(null);

  // Export is only offered for Privy embedded wallets — external wallets
  // (MetaMask, etc.) already control their own private key.
  const { exportWallet } = useExportWallet();
  const { wallets } = useWallets();
  const embeddedWallet = getEmbeddedConnectedWallet(wallets);
  const isEmbeddedWallet =
    !!profile?.address && embeddedWallet?.address.toLowerCase() === profile.address.toLowerCase();

  // $BIGW balance, read from chain while the modal is open, for any connected
  // wallet (embedded or external — the balance is just an on-chain read).
  const { balance, loading: balanceLoading } = useBigwBalance(profile?.address, open);

  // Mount the Telegram widget directly when the modal opens — the iframe is
  // sized to its final dimensions inside the hook, so there's no slide-in
  // animation while Telegram negotiates its content size.
  const tgAlreadyLinked = !!profile?.linkedSocials.telegram;
  const tgWidget = useTelegramLoginWidget(
    tgContainerRef,
    open && isBackendConfigured && !tgAlreadyLinked,
  );
  const unlinkTg = useUnlinkTelegram();
  const linkDc = useLinkDiscord();
  const unlinkDc = useUnlinkDiscord();
  const linkX = useLinkX();
  const unlinkXAccount = useUnlinkX();
  const { discord, reason: discordReason } = useDiscordCallbackStatus();
  const { x: xStatus, reason: xReason } = useXCallbackStatus();

  // A single OAuth redirect carries at most one provider status; prefer
  // whichever one is present.
  const notice =
    socialCallbackNotice('Discord', discord, discordReason) ??
    socialCallbackNotice('X', xStatus, xReason);

  // The redirect lands on a fresh page load with the modal closed, so surface
  // the outcome by re-opening the profile where the user started the link.
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  useEffect(() => {
    if (discord || xStatus) {
      openProfile();
      setNoticeDismissed(false);
    }
  }, [discord, xStatus, discordReason, xReason, openProfile]);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);

  if (!open) return null;

  const onCopyAddress = async (): Promise<void> => {
    if (!profile?.address) return;
    try {
      await navigator.clipboard.writeText(profile.address);
      setCopied(true);
    } catch {
      /* clipboard unavailable — silent */
    }
  };

  const onExportWallet = async (): Promise<void> => {
    if (!profile?.address) return;
    try {
      await exportWallet({ address: profile.address });
    } catch {
      /* user closed the export modal — silent */
    }
  };

  const onUnlinkSocial = (provider: SocialProvider): void => {
    if (provider === 'telegram') unlinkTg.mutate();
    else if (provider === 'discord') unlinkDc.mutate();
    else if (provider === 'x') unlinkXAccount.mutate();
  };

  const onLinkDiscord = (): void => {
    linkDc.start();
  };

  const onSignOut = async (): Promise<void> => {
    await logout();
    await runPrivyLogout();
    reset();
    // Wallet-scoped caches (score, submissions, cooldowns, ...) aren't keyed
    // by wallet address — without this, signing into a different wallet
    // within the staleTime window would show the previous wallet's data.
    queryClient.clear();
    closeProfile();
  };

  return (
    <Modal
      open={open}
      onClose={closeProfile}
      labelledBy="profile-modal-title"
      className="w-full max-w-md"
      showClose={false}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line bg-white px-6 py-4">
        <h2
          id="profile-modal-title"
          className="font-display text-xl font-bold tracking-tightish text-ink-950"
        >
          Profile
        </h2>
        <button
          type="button"
          onClick={closeProfile}
          aria-label="Close profile"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate2-600 transition-colors hover:bg-paper-warm hover:text-ink-950"
        >
          <XIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* OAuth link result (Discord / X) */}
      {notice && !noticeDismissed && (
        <div
          role="status"
          aria-live="polite"
          className={cn(
            'mx-6 mt-5 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm',
            notice.tone === 'success'
              ? 'border-forest-100 bg-forest-50 text-forest-700'
              : 'border-rose2-100 bg-rose2-50 text-rose2-600',
          )}
        >
          {notice.tone === 'success' ? (
            <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          <p className="flex-1">
            {notice.message}
            {/* The provider sent the user back to a browser that isn't
                signed in (e.g. it left an in-app browser) — the link itself
                still went through for the account that started it. */}
            {notice.tone === 'success' && !profile
              ? " You can go back to where you started — it's linked there too."
              : null}
          </p>
          <button
            type="button"
            onClick={() => setNoticeDismissed(true)}
            aria-label="Dismiss message"
            className="shrink-0 opacity-60 transition-opacity hover:opacity-100"
          >
            <XIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      )}

      {profile ? (
        <>
          {/* Identity card */}
          <div className="bw-card bw-card-water mx-6 mt-5 p-5">
            <div className="flex items-start gap-4">
              <Avatar src={profile.avatarUrl} name={profile.displayName} size={56} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-base font-semibold text-ink-950">
                  {profile.displayName}
                </p>
                <div className="mt-1.5 flex items-center gap-2">
                  <code className="rounded-md border border-line bg-white px-2 py-0.5 font-mono text-xs text-slate2-700">
                    {truncateAddress(profile.address)}
                  </code>
                  <button
                    type="button"
                    onClick={onCopyAddress}
                    aria-label={copied ? 'Address copied to clipboard' : 'Copy wallet address'}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate2-600 transition-colors hover:bg-white hover:text-ink-950"
                  >
                    {copied ? (
                      <Check className="h-3.5 w-3.5 text-forest-600" aria-hidden="true" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                  </button>
                  {isEmbeddedWallet && (
                    <button
                      type="button"
                      onClick={onExportWallet}
                      aria-label="Export wallet"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate2-600 transition-colors hover:bg-white hover:text-ink-950"
                    >
                      <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                  <span className="sr-only" aria-live="polite">
                    {copied ? 'Address copied to clipboard' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Wallet card: $BIGW balance (all wallets) + hosted portal (embedded only) */}
          <section className="mx-6 mt-4 rounded-2xl border border-line bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <img src="/logomark.svg" alt="" className="h-9 w-9 shrink-0 rounded-lg" />
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate2-500">
                    Balance
                  </p>
                  {balance !== null ? (
                    <p className="num-tabular font-display text-xl font-semibold leading-tight text-ink-950">
                      {balance} <span className="text-sm font-medium text-slate2-500">BIGW</span>
                    </p>
                  ) : balanceLoading ? (
                    <Loader2 className="mt-1 h-4 w-4 animate-spin text-slate2-400" aria-hidden="true" />
                  ) : (
                    <p className="font-display text-base font-semibold leading-tight text-ink-950">
                      BigWater wallet
                    </p>
                  )}
                </div>
              </div>
              {isEmbeddedWallet && (
                <a
                  href={walletPortalUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-water inline-flex shrink-0 items-center gap-1.5 px-3.5 py-2 text-sm transition active:scale-[0.98]"
                >
                  Manage wallet
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              )}
            </div>
            {/* Buy / Stake — keep prompting the user to load up; staking ships later */}
            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-line pt-3">
              <a
                href="https://bigwater.io/buy.html"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-water px-3 py-2 text-sm"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Buy $BIGW
              </a>
              <button
                type="button"
                disabled
                aria-disabled="true"
                title="Staking is coming soon"
                className="inline-flex cursor-not-allowed items-center justify-center gap-1.5 rounded-xl border border-line bg-paper-warm px-3 py-2 text-sm font-semibold text-slate2-400"
              >
                Stake
                <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate2-500">
                  Soon
                </span>
              </button>
            </div>
            {isEmbeddedWallet && (
              <div className="mt-3 border-t border-line pt-2.5">
                <a
                  href="https://bigwater.io/wallet-guide"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-slate2-500 underline-offset-2 hover:text-ink-950 hover:underline"
                >
                  Wallet guide
                </a>
              </div>
            )}
          </section>

          {/* Stats grid */}
          <dl className="mx-6 mt-4 grid grid-cols-3 gap-3">
            <Stat label="Points" value={formatPoints(participant?.score ?? 0)} />
            <Stat label="Rank" value={participant ? `#${participant.rank}` : '—'} />
            <Stat
              label="Missions"
              value={`${missionsCompleted}/${missionsTotal ?? '—'}`}
            />
          </dl>

          {/* Linked socials */}
          <section
            className="mx-6 mt-5 overflow-hidden rounded-2xl border border-line bg-white"
            aria-labelledby="linked-socials-title"
          >
            <h3
              id="linked-socials-title"
              className="border-b border-line bg-paper-warm px-4 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-slate2-600"
            >
              Linked socials
            </h3>
            {isInAppBrowser() &&
              SOCIALS.some(({ provider }) => !profile.linkedSocials[provider]) && (
                <InAppBrowserHint />
              )}
            <ul>
              {SOCIALS.map(({ provider, label, Icon }, idx) => {
                const handle = profile.linkedSocials[provider];
                const linked = Boolean(handle);
                const noBackend = !isBackendConfigured;
                return (
                  <li
                    key={provider}
                    className={cn(
                      'flex items-center justify-between gap-3 px-4 py-3',
                      idx > 0 && 'border-t border-line',
                    )}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="grid h-9 w-9 place-items-center rounded-full bg-paper-warm text-ink-950">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-ink-950">{label}</p>
                        {linked ? (
                          <p className="truncate font-mono text-xs text-slate2-600">
                            {handle ? `@${handle}` : 'Linked'}
                          </p>
                        ) : (
                          <p className="text-xs text-slate2-500">Not connected</p>
                        )}
                      </div>
                    </div>

                    {linked ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onUnlinkSocial(provider)}
                        disabled={noBackend}
                      >
                        Disconnect
                      </Button>
                    ) : provider === 'telegram' ? (
                      <div className="flex flex-col items-end gap-1">
                        <div ref={tgContainerRef} className="min-h-[28px] text-right">
                          {!isBackendConfigured && (
                            <span className="text-xs text-slate2-500">Unavailable</span>
                          )}
                          {isBackendConfigured && tgWidget.linking && (
                            <span className="text-xs text-slate2-500">Linking…</span>
                          )}
                        </div>
                        {tgWidget.error && (
                          <span className="text-[11px] text-rose2-600">{tgWidget.error}</span>
                        )}
                      </div>
                    ) : provider === 'discord' ? (
                      <div className="flex flex-col items-end gap-1">
                        <button
                          type="button"
                          onClick={onLinkDiscord}
                          disabled={!isBackendConfigured || linkDc.pending}
                          aria-busy={linkDc.pending}
                          className="btn-water px-3 py-1.5 text-xs disabled:opacity-50"
                        >
                          {linkDc.pending ? 'Redirecting…' : 'Link'}
                        </button>
                        {linkDc.error && (
                          <span role="alert" className="text-right text-[11px] text-rose2-600">
                            {linkDc.error}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-end gap-1">
                        <button
                          type="button"
                          onClick={() => linkX.start()}
                          disabled={!isBackendConfigured || linkX.pending}
                          aria-busy={linkX.pending}
                          className="btn-water px-3 py-1.5 text-xs disabled:opacity-50"
                        >
                          {linkX.pending ? 'Redirecting…' : 'Link'}
                        </button>
                        {linkX.error && (
                          <span role="alert" className="text-right text-[11px] text-rose2-600">
                            {linkX.error}
                          </span>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      ) : (
        <div className="mx-6 my-6 rounded-2xl border border-line bg-white px-4 py-6 text-center text-sm text-slate2-600">
          No profile loaded. Connect your wallet to view your account.
        </div>
      )}

      {/* Footer */}
      <div className="mt-5 border-t border-line bg-white px-6 py-4">
        <button
          type="button"
          onClick={onSignOut}
          className="btn-ghost w-full justify-center text-rose2-600 hover:bg-rose2-50"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          <span>Sign out</span>
        </button>
      </div>
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-paper-warm px-4 py-3">
      <dt className="font-mono text-[10px] font-semibold uppercase tracking-wide text-slate2-600">
        {label}
      </dt>
      <dd className="mt-1 num-tabular font-display text-base font-semibold text-ink-950">
        {value}
      </dd>
    </div>
  );
}

export default ProfileModal;
